import React, {useMemo, useState} from 'react';
import {Card, Col, Input, Row, Select, Space, Typography} from 'antd';
import {FormattedMessage, useIntl} from 'react-intl';
import {BookOutlined} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import DetailWrapper from '../components/common/DetailWrapper';
import {HELP_ARTICLES, HELP_CATEGORIES} from '../constants/helpArticles';

const {Title, Text, Paragraph} = Typography;
const {Search} = Input;

const HelpPage: React.FC = () => {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState<string | null>(null);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return HELP_ARTICLES.filter(a => {
            const title = intl.formatMessage({id: a.titleKey}).toLowerCase();
            const desc = intl.formatMessage({id: a.descriptionKey}).toLowerCase();
            const body = a.bodyBlocks.map(b => intl.formatMessage({id: b.key})).join(' ').toLowerCase();
            const matchesSearch = !q || title.includes(q) || desc.includes(q) || body.includes(q);
            const matchesCategory = !category || a.category === category;
            return matchesSearch && matchesCategory;
        });
    }, [search, category, intl]);

    const categoryOptions = [
        {value: null as string | null, label: intl.formatMessage({id: 'all_categories'})},
        ...HELP_CATEGORIES.map(c => ({value: c, label: intl.formatMessage({id: `help_category_${c}`})})),
    ];

    return (
        <DetailWrapper navigateBack={undefined}>
            <Row className="mb-2">
                <Col span={24}>
                    <h3>
                        <span className="text-blue mr-8"><BookOutlined/></span>
                        <FormattedMessage id="help_title"/>
                    </h3>
                </Col>
            </Row>
            <Row gutter={16} className="mb-4">
                <Col xs={24} md={14} style={{marginBottom: 8}}>
                    <Search
                        placeholder={intl.formatMessage({id: 'help_search_placeholder'})}
                        allowClear
                        onChange={e => setSearch(e.target.value)}
                        size="large"
                    />
                </Col>
                <Col xs={24} md={8} style={{marginBottom: 8}}>
                    <Select
                        style={{width: '100%'}}
                        size="large"
                        value={category}
                        onChange={setCategory}
                        options={categoryOptions}
                    />
                </Col>
            </Row>
            {filtered.length === 0 ? (
                <Text type="secondary"><FormattedMessage id="no_data"/></Text>
            ) : (
                <Row gutter={[16, 16]}>
                    {filtered.map(article => (
                        <Col xs={24} md={12} lg={8} key={article.slug}>
                            <Link to={`/help/${article.slug}`} style={{display: 'block', height: '100%'}}>
                                <Card hoverable style={{height: '100%'}}>
                                    <Space direction="vertical" size="small" style={{width: '100%'}}>
                                        <Text
                                            type="secondary"
                                            style={{fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5}}
                                        >
                                            <FormattedMessage id={`help_category_${article.category}`}/>
                                        </Text>
                                        <Title level={5} style={{margin: 0}}>
                                            <FormattedMessage id={article.titleKey}/>
                                        </Title>
                                        <Paragraph type="secondary" style={{margin: 0}}>
                                            <FormattedMessage id={article.descriptionKey}/>
                                        </Paragraph>
                                    </Space>
                                </Card>
                            </Link>
                        </Col>
                    ))}
                </Row>
            )}
        </DetailWrapper>
    );
};

export default HelpPage;
